import { Fragment, useCallback, useEffect, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import {
  busbarSelectionRunCreateRequestSchema,
  type BusbarSelectionRunCreateRequest,
} from "../services/busbarSelection";
import type { SldElements } from "../services/sldElements";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import {
  BUSBAR_SELECTION_DRAFT_MODULE,
  BUSBAR_SELECTION_LABELS,
  buildBusbarSelectionPayload,
  busbarSelectionDraftSchema,
  createBuswayRowDraft,
  createDeratingFactorDraft,
  initialBusbarSelectionDraft,
  seedRowIdCounter,
  type BusbarSelectionDraft,
  type BuswayRowDraft,
  type DeratingFactorDraft,
} from "./busbarSelectionDraft";
import { AmpacityBlock } from "./BusbarSelectionAmpacity";
import {
  ClearanceBlock,
  NeutralBlock,
  ProtectiveEarthBlock,
  SelectedArrangementBlock,
  ShortCircuitBlock,
  SupportsBlock,
  TemperatureRiseBlock,
} from "./BusbarSelectionBlocks";
import { BuswayBlock } from "./BusbarSelectionBusway";
import { BusbarBasics, ClosingFields, FaultLevel, StudyDefinition } from "./BusbarSelectionCore";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { sldElementsOf } from "./SldElementPicker";

// The words of the fault-run selector live with the shared selector; re-exported here for the
// pages and tests that read them from this form.
export {
  FAULT_RUN_NOT_LINKABLE,
  NO_FAULT_RUNS_NOTE,
  NO_OPEN_REVISION_NOTE,
  NOT_MAXIMUM_THREE_PHASE_NOTE,
  busMismatchNote,
  faultRunOptionText,
} from "./FaultRunSelector";
export {
  MARGIN_FACTOR_HINT,
  NO_PEAK_NOTE,
  RUN_NOTE_HINT,
  STUDY_NOTES_HINT,
  TYPED_HINT,
} from "./BusbarSelectionCore";

type BusbarSelectionFormProps = {
  disabled?: boolean;
  onSubmit: (payload: BusbarSelectionRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): the bus is
  // picked from it and its voltage fills the typed system voltage while that is blank.
  sldElements?: SldElements | null | undefined;
};

// The LV busbar selection study (EOS-07c). The form owns the draft and composes the blocks;
// each block shows its own slice of the draft and renders again only when that slice changes.
// The optional blocks are collapsed until needed; the busway block is shown for a BUSWAY study
// only, its rows staying in the draft while it is hidden. The form holds no engineering rule:
// the backend engine is the single source of truth, and its message is shown as it words it.
//
// The draft is kept under its own module key, "busbar-selection". The payload carries no
// project revision: the page adds the selected revision when it calls the API, exactly as the
// other study pages do.
export function BusbarSelectionForm({
  disabled = false,
  onSubmit,
  sldElements,
}: BusbarSelectionFormProps) {
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<BusbarSelectionDraft>({
      module: BUSBAR_SELECTION_DRAFT_MODULE,
      createInitial: initialBusbarSelectionDraft,
      schema: busbarSelectionDraftSchema,
      revisionId,
    });

  const [messages, setMessages] = useState<string[]>([]);
  // A block starts open when it holds a value, decided when it appears. Clearing the form
  // gives the optional blocks a new key, so they decide again from the empty draft.
  const [generation, setGeneration] = useState(0);

  // The rows of a restored draft keep the ids they were saved with; a row added afterwards
  // must not take one of them.
  useEffect(() => {
    seedRowIdCounter([...draft.deratingFactors, ...draft.buswayRows].map((row) => row.id));
  }, [draft.deratingFactors, draft.buswayRows]);

  // Stable, so a block that did not change does not render again.
  const updateStudy = useCallback(
    (patch: Partial<BusbarSelectionDraft>) => setDraft((current) => ({ ...current, ...patch })),
    [setDraft],
  );

  const setFaultRunId = useCallback(
    (faultRunId: string) => setDraft((current) => ({ ...current, faultRunId })),
    [setDraft],
  );

  // The bus picked from the diagram; its voltage fills the typed system voltage while that is
  // still blank, never over a value the engineer typed.
  const chooseBus = useCallback(
    (busCode: string) => {
      const bus = sldElementsOf(sldElements, ["bus"]).find((element) => element.code === busCode);
      setDraft((current) => ({
        ...current,
        busCode,
        typedSystemNominalVoltageV:
          current.typedSystemNominalVoltageV.trim() === "" && bus?.voltage_v
            ? bus.voltage_v
            : current.typedSystemNominalVoltageV,
      }));
    },
    [setDraft, sldElements],
  );

  const addDeratingFactor = useCallback(
    () =>
      setDraft((current) => ({
        ...current,
        deratingFactors: [...current.deratingFactors, createDeratingFactorDraft()],
      })),
    [setDraft],
  );

  const changeDeratingFactor = useCallback(
    (rowId: string, patch: Partial<DeratingFactorDraft>) =>
      setDraft((current) => ({
        ...current,
        deratingFactors: current.deratingFactors.map((row) =>
          row.id === rowId ? { ...row, ...patch } : row,
        ),
      })),
    [setDraft],
  );

  const removeDeratingFactor = useCallback(
    (rowId: string) =>
      setDraft((current) => ({
        ...current,
        deratingFactors: current.deratingFactors.filter((row) => row.id !== rowId),
      })),
    [setDraft],
  );

  const addBuswayRow = useCallback(
    () =>
      setDraft((current) => ({
        ...current,
        buswayRows: [...current.buswayRows, createBuswayRowDraft()],
      })),
    [setDraft],
  );

  const changeBuswayRow = useCallback(
    (rowId: string, patch: Partial<BuswayRowDraft>) =>
      setDraft((current) => ({
        ...current,
        buswayRows: current.buswayRows.map((row) =>
          row.id === rowId ? { ...row, ...patch } : row,
        ),
      })),
    [setDraft],
  );

  const removeBuswayRow = useCallback(
    (rowId: string) =>
      setDraft((current) => ({
        ...current,
        buswayRows: current.buswayRows.filter((row) => row.id !== rowId),
      })),
    [setDraft],
  );

  // Empties the form only; a result already on the page stays until "Clear results".
  function clearForm() {
    setMessages([]);
    setGeneration((current) => current + 1);
    clear();
  }

  // The selector's own sentence stands alone; every other issue is read by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    if (issue.code === "custom" && issue.path.at(-1) === "fault_run_id") {
      return issue.message;
    }
    return describeValidationIssue(issue, BUSBAR_SELECTION_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessages([]);

    const parsed = busbarSelectionRunCreateRequestSchema.safeParse(
      buildBusbarSelectionPayload(draft),
    );

    if (!parsed.success) {
      // The schema lists its issues in the order of the request, which is the order of the
      // form. A blank decimal fails two of its checks ("is required" and "plain number"); the
      // first sentence of a field is the one shown, and a sentence two fields share only once.
      const seenFields = new Set<string>();
      const firstOfField = parsed.error.issues.filter((issue) => {
        const field = issue.path.join(".");
        if (seenFields.has(field)) {
          return false;
        }
        seenFields.add(field);
        return true;
      });
      const sentences = [...new Set(firstOfField.map(describeIssue))];
      setMessages(sentences.length > 0 ? sentences : ["Review the busbar selection inputs."]);
      return;
    }

    await onSubmit(parsed.data);
  }

  return (
    <form aria-label="Busbar selection inputs" onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice
          onKeep={keepForThisRevision}
          onClear={clearForm}
          disabled={disabled}
        />
      ) : null}

      <StudyDefinition
        draft={draft}
        onChange={updateStudy}
        disabled={disabled}
        sldElements={sldElements}
        onChooseBus={chooseBus}
      />
      <FaultLevel
        draft={draft}
        onChange={updateStudy}
        disabled={disabled}
        revisionId={revisionId}
        onFaultRunChange={setFaultRunId}
      />
      <BusbarBasics draft={draft} onChange={updateStudy} disabled={disabled} />

      <Fragment key={generation}>
        <SelectedArrangementBlock draft={draft} onChange={updateStudy} disabled={disabled} />
        <ShortCircuitBlock draft={draft} onChange={updateStudy} disabled={disabled} />
        <AmpacityBlock
          draft={draft}
          onChange={updateStudy}
          disabled={disabled}
          onAddRow={addDeratingFactor}
          onRowChange={changeDeratingFactor}
          onRowRemove={removeDeratingFactor}
        />
        <TemperatureRiseBlock draft={draft} onChange={updateStudy} disabled={disabled} />
        <SupportsBlock draft={draft} onChange={updateStudy} disabled={disabled} />
        <NeutralBlock draft={draft} onChange={updateStudy} disabled={disabled} />
        <ProtectiveEarthBlock draft={draft} onChange={updateStudy} disabled={disabled} />
        <ClearanceBlock draft={draft} onChange={updateStudy} disabled={disabled} />
        {draft.systemKind === "BUSWAY" ? (
          <BuswayBlock
            draft={draft}
            onChange={updateStudy}
            disabled={disabled}
            onAddRow={addBuswayRow}
            onRowChange={changeBuswayRow}
            onRowRemove={removeBuswayRow}
          />
        ) : null}
      </Fragment>

      <ClosingFields draft={draft} onChange={updateStudy} disabled={disabled} />

      {messages.length > 0 ? (
        <ul role="alert">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : null}

      <button disabled={disabled} type="submit">
        Check busbar selection
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default BusbarSelectionForm;
